import { Button } from "@nabble/ui";
import { useState } from "react";
import type { AccountInfo } from "../../../../api/account";
import type { AccountEmail } from "../../../../api/accountEmails";
import { AddEmailDialog } from "./AddEmailDialog";
import { MAX_EMAILS } from "./emailErrors";
import { MakePrimaryDialog } from "./MakePrimaryDialog";
import { OtherEmailItem } from "./OtherEmailItem";
import { RemoveEmailDialog } from "./RemoveEmailDialog";
import { useResendConfirmation } from "./useResendConfirmation";

const LIST_STYLE = { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-3)" } as const;

/** Emails → Other emails: the secondary addresses, up to MAX_EMAILS, and what each one allows. */
export function OtherEmailsRow({ account, onChanged }: { account: AccountInfo; onChanged: () => void }): React.JSX.Element {
  const [addOpen, setAddOpen] = useState(false);
  const [primaryTarget, setPrimaryTarget] = useState<AccountEmail | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AccountEmail | null>(null);
  const { resending, resend } = useResendConfirmation();
  const full = account.emails.length >= MAX_EMAILS;

  function closeAnd(close: () => void): () => void {
    return () => {
      close();
      onChanged();
    };
  }

  return (
    <>
      <div className="nb-setting-row is-stacked">
        <div className="nb-setting-head">
          <div>
            <div className="nb-setting-title">Other emails</div>
            <p className="nb-setting-desc">Confirmed secondary emails can also be used to recover your password.</p>
          </div>
          <Button label="Add email" variant="secondary" size="sm" icon="plus" disabled={full} onClick={() => setAddOpen(true)} />
        </div>
        {account.emails.length > 0 ? (
          <ul aria-label="Other emails" style={LIST_STYLE}>
            {account.emails.map((item) => (
              <OtherEmailItem
                key={item.id}
                item={item}
                resending={resending === item.id}
                onMakePrimary={() => setPrimaryTarget(item)}
                onResend={() => void resend(item)}
                onRemove={() => setRemoveTarget(item)}
              />
            ))}
          </ul>
        ) : (
          <p className="nb-help">You haven&apos;t added any other emails.</p>
        )}
        {full ? <p className="nb-help">{`You can have up to ${MAX_EMAILS} other emails. Remove one to add another.`}</p> : null}
      </div>

      {addOpen ? <AddEmailDialog onClose={() => setAddOpen(false)} onAdded={closeAnd(() => setAddOpen(false))} /> : null}

      {primaryTarget ? (
        <MakePrimaryDialog
          target={primaryTarget}
          current={account.email}
          onClose={() => setPrimaryTarget(null)}
          onDone={closeAnd(() => setPrimaryTarget(null))}
        />
      ) : null}

      <RemoveEmailDialog target={removeTarget} onClose={() => setRemoveTarget(null)} onRemoved={closeAnd(() => setRemoveTarget(null))} />
    </>
  );
}
